import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { listNotifications, getUnreadCount, markNotifRead, markAllNotifRead } from "@/lib/api";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
} from "@/components/ui/dropdown-menu";
import { Bell, CheckCheck } from "lucide-react";

const dt = (s) => (s ? String(s).slice(0, 16).replace("T", " ") : "");

export const NotificationBell = () => {
  const { t } = useApp();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);

  const load = useCallback(async () => {
    try {
      const [n, c] = await Promise.all([listNotifications(), getUnreadCount()]);
      setItems(Array.isArray(n) ? n : []);
      setCount(c?.count || 0);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    load();
    const iv = setInterval(load, 30000);
    return () => clearInterval(iv);
  }, [load]);

  const onClick = async (it) => {
    if (!it.read) {
      try { await markNotifRead(it.id); } catch { /* ignore */ }
      setCount((c) => Math.max(0, c - 1));
    }
    setItems((arr) => arr.map((x) => (x.id === it.id ? { ...x, read: true } : x)));
    if (it.link) navigate(it.link);
  };

  const readAll = async () => {
    try { await markAllNotifRead(); } catch { /* ignore */ }
    setItems((arr) => arr.map((x) => ({ ...x, read: true })));
    setCount(0);
  };

  return (
    <DropdownMenu onOpenChange={(o) => o && load()}>
      <DropdownMenuTrigger
        data-testid="notif-bell"
        className="relative flex h-9 w-9 items-center justify-center rounded-md border border-border text-neutral-700 outline-none transition-colors hover:border-primary hover:text-primary">
        <Bell className="h-4 w-4" />
        {count > 0 && (
          <span
            data-testid="notif-badge"
            className="absolute -right-1 -top-1 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-accent px-1 text-[10px] font-bold text-accent-foreground">
            {count > 9 ? "9+" : count}
          </span>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 bg-white p-0" data-testid="notif-dropdown">
        <div className="flex items-center justify-between border-b border-border px-3 py-2">
          <span className="text-sm font-semibold text-neutral-800">{t("notif.title")}</span>
          {items.some((x) => !x.read) && (
            <button onClick={readAll} data-testid="notif-markall" className="inline-flex items-center gap-1 text-xs text-primary hover:underline">
              <CheckCheck className="h-3.5 w-3.5" /> {t("notif.markAll")}
            </button>
          )}
        </div>
        <div className="max-h-96 overflow-y-auto">
          {items.length === 0 ? (
            <p className="px-3 py-8 text-center text-sm text-neutral-400" data-testid="notif-empty">{t("notif.empty")}</p>
          ) : (
            items.map((it) => (
              <button
                key={it.id}
                onClick={() => onClick(it)}
                data-testid={`notif-item-${it.id}`}
                className={`flex w-full flex-col items-start gap-0.5 border-b border-border/60 px-3 py-2.5 text-start transition-colors hover:bg-secondary ${it.read ? "" : "bg-accent/5"}`}>
                <div className="flex w-full items-center gap-2">
                  {!it.read && <span className="h-2 w-2 shrink-0 rounded-full bg-accent" />}
                  <span className="flex-1 truncate text-sm font-medium text-neutral-800">{it.title}</span>
                </div>
                {it.body && <span className="line-clamp-1 text-xs text-neutral-500">{it.body}</span>}
                <span className="text-[11px] text-neutral-400">{dt(it.created_at)}</span>
              </button>
            ))
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
